"use client";

import React from "react";
const features = [{
  title: "AI-Native Infrastructure Design",
  desc: "High-density, rack-scale infrastructure engineered for H100/GB300 chipsets, cluster architectures, and next-generation liquid cooling standards."
}, {
  title: "Token Utilization Efficiency (TUE)",
  desc: "We focus on TUE — not traditional PUE — to capture maximum compute value and drive premium exit multiples for investors."
}, {
  title: "ESG-Integrated Investment Model",
  desc: "PUE <1.3 with renewable PPAs and ESG-aligned financing structures, enhancing yield resilience and LP diversification."
}, {
  title: "Project-Level SPV Governance",
  desc: "Platform structured via SPVs under centralized governance to optimize capital efficiency and maximize exit optionality."
}];
const metrics = [{
  value: "$5B+",
  label: "Platform Revenue Supported",
  subtext: "\n",
  dark: false
}, {
  value: "120+",
  label: "Data Centers",
  subtext: "Global operations",
  dark: true
}, {
  value: "45+",
  label: "Metro Cities",
  subtext: "Global infrastructure footprint",
  dark: false
}, {
  value: "24",
  label: "Countries",
  subtext: "Americas · Mexico · Asia-Pacific",
  dark: false
}, {
  value: "~1.2GW",
  label: "Critical IT Capacity",
  subtext: "\n",
  dark: false
}, {
  value: "5",
  label: "Granted Patents",
  subtext: "AI DC · Sustainable Infra · Auto Ops",
  dark: false
}];
export default function AboutSection() {
  return <section className="relative overflow-hidden bg-[#FDFAF3] pt-36 pb-24 md:pt-44 text-[#1C1917]">
      
      {/* Glow */}
      <div className="pointer-events-none absolute left-0 top-0 h-[620px] w-[620px] -translate-x-1/3 -translate-y-1/3 rounded-full bg-[#E7D8B8]/55 blur-[100px]" />

      <div className="relative z-10 mx-auto grid max-w-[1500px] grid-cols-1 gap-16 px-6 lg:grid-cols-[0.95fr_1.25fr] lg:px-8">
        
        {/* LEFT SIDE */}
        <div>
          <div className="mb-6 flex items-center gap-3">
            <span className="h-px w-8 bg-[#B45309]" />
            <span className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-[#B45309]">
              About N2N Capital
            </span>
          </div>

          {/* FIXED HEADING */}
          <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl font-semibold leading-[1.1] tracking-tight text-[#1C1917]">
            Operator-Built.
            <br />
            <span className="text-[#B45309]">
              Institutionally Structured.
            </span>
          </h2>

          <p className="mt-8 max-w-2xl text-lg leading-relaxed text-[#1C1917]/65">
            N2N Capital combines private equity discipline with hyperscale
            operating expertise to deliver contracted, infrastructure-grade cash
            flows and scalable platform value creation. We develop proprietary
            AI-native infrastructure across the U.S., India, and Mexico.
          </p>

          {/* FEATURES */}
          <div className="mt-14 space-y-0">
            {features.map((item, i) => <div key={item.title} className="border-t border-[#1C1917]/30 py-7 last:border-b">
                <div className="grid grid-cols-[42px_1fr] gap-3">
                  <span className="font-mono text-sm font-bold text-[#B45309]">
                    {String(i + 1).padStart(2, "0")}
                  </span>

                  <div>
                    {/* FIXED FEATURE FONT */}
                    <h4 className="font-sans text-lg font-semibold text-[#1C1917]">
                      {item.title}
                    </h4>

                    <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[#1C1917]/60">
                      {item.desc}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>
        </div>

        {/* RIGHT SIDE */}
        <div className="pt-10 lg:pt-14">
          <div className="border-t border-[#D8C29A] pt-8">

            <div className="overflow-hidden rounded-xl border border-[#E7D8B8] bg-[#FDFAF3] shadow-sm">
              
              <div className="grid gap-0 xl:grid-cols-[0.95fr_1.35fr]">
                
                {/* LEFT PANEL */}
                <div className="p-10 lg:p-12">
                  
                  <h3 className="mt-6 font-serif text-4xl md:text-5xl font-semibold leading-[1.05] tracking-tight">
                    +10 Years
                    <br />
                    Building
                    <br />
                    <span className="text-[#B45309]">Global AI</span>
                    <br />
                    Infrastructure
                  </h3>

                  {/* LOGOS */}
                  <div className="mt-12 border-t border-[#E7D8B8] pt-8">
                    <div className="flex items-center gap-10">
                      
                      <div className="flex items-center">
                        <img src="https://decent-kid.10web.cloud/wp-content/uploads/2026/04/Equinix-Logo.wine_-scaled.png" alt="Equinix logo" className="h-20 w-auto object-contain opacity-90" />
                      </div>

                      <div>
                        <div className="mb-3 flex h-7 items-end gap-1.5">
                          {[10, 18, 26, 14, 22, 12, 28].map((h, i) => <div key={i} className="w-1.5 rounded-full bg-[#2DAAE1]" style={{
                          height: `${h}px`
                        }} />)}
                        </div>

                        <p className="font-serif text-xl font-bold tracking-[0.18em] text-[#2DAAE1]">
                          CISCO
                        </p>
                      </div>

                    </div>
                  </div>
                </div>

                {/* METRICS GRID */}
                <div className="grid grid-cols-2 bg-[#F5EDD8] p-[2px]">
                  {metrics.map((metric, i) => <div key={metric.label} className={`min-h-[200px] border border-[#E7D8B8] p-8 ${metric.dark ? "bg-[#1C1917] text-[#FDFAF3]" : i === 0 || i === 5 ? "bg-[#B45309] text-white" : "bg-[#F5EDD8] text-[#1C1917]"}`}>
                      <div className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] opacity-75">
                        {metric.label}
                      </div>

                      <div className="mt-6 font-serif font-semibold leading-none text-4xl">
                        {metric.value}
                      </div>

                      <p className="mt-4 max-w-[200px] text-sm leading-relaxed opacity-65">
                        {metric.subtext}
                      </p>
                    </div>)}
                </div>

              </div>
            </div>

            <p className="mt-4 text-xs leading-relaxed text-[#1C1917]/35">
              * Platform metrics reflect contributions during senior leadership
              tenure and represent the scale of operations supported — not
              assets under Management.
            </p>

          </div>
        </div>
      </div>
    </section>;
}